/**
 * OwnerScopeDefault — first-run scope default for owners/admins.
 *
 * The view-scope preference defaults to "self" for everyone. That's correct for
 * reps (they see their own attributed work), but wrong for an owner/admin: a
 * solo or small-firm owner whose CRM rows are owned by external user-ids (e.g.
 * GoHighLevel) ends up with an empty "self" view — $0 pipeline, no signals, an
 * empty briefing — until they discover the scope picker.
 *
 * This applies a one-time default of "org" for owner/admin users who have never
 * explicitly chosen a scope (checked against the server-side preference, the
 * source of truth — so an explicit "self" choice is always respected). It only
 * ever BROADENS scope for already-privileged roles; it can never widen a
 * member's view. Renders nothing.
 */
import { useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api';
import { useMe, useMeLoaded } from '../lib/hooks/use-me';
import { useViewScope } from '../lib/hooks/view-scope';

interface PreferencesResponse {
  preferences: Record<string, unknown>;
}

export function OwnerScopeDefault() {
  const me = useMe();
  const meLoaded = useMeLoaded();
  const { setScope } = useViewScope();
  const applied = useRef(false);

  // Shares the cache key usePreference uses, so this is not an extra request.
  const prefs = useQuery({
    queryKey: ['user-preferences'],
    queryFn: () => api<PreferencesResponse>('/v1/preferences'),
    staleTime: 60_000,
    retry: 1,
    enabled: meLoaded,
  });

  useEffect(() => {
    if (applied.current) return;
    if (!meLoaded || !prefs.isSuccess) return;
    applied.current = true;
    const everChosen = prefs.data?.preferences?.view_scope != null;
    const isOrgRole = me.role === 'owner' || me.role === 'admin';
    if (!everChosen && isOrgRole) {
      setScope('org');
    }
  }, [meLoaded, prefs.isSuccess, prefs.data, me.role, setScope]);

  return null;
}
